'use client'

import { useMemo } from 'react'
import { Command } from 'cmdk'
import { HugeiconsIcon } from '@hugeicons/react'
import {
  Megaphone02Icon,
  VideoCameraAiIcon,
  TimeScheduleIcon,
  PlusSignIcon,
  Message01Icon,
  Loading03Icon,
} from '@hugeicons-pro/core-stroke-rounded'
import { TYPE_LABELS, NEW_SHORTCUTS, CAMPAIGN_STATUS_COLORS, filterNavShortcuts } from './constants'
import type { SearchResultGroup } from '@/hooks/useCommandSearchState'

const groupHeadingClass =
  '[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-[10px] [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wide [&_[cmdk-group-heading]]:text-[#939294]'

function CampaignStatusBadge({ status }: { status: string }) {
  const color = CAMPAIGN_STATUS_COLORS[status.toLowerCase()] ?? 'bg-neutral-400'
  return (
    <span className="ml-2 flex items-center gap-1 shrink-0">
      <span className={`inline-block size-1.5 rounded-full ${color}`} />
      <span className="text-xs capitalize text-[#939294]">{status}</span>
    </span>
  )
}

function ResultIcon({ type }: { type: string }) {
  const cls = 'size-4 shrink-0 text-[#939294]'
  switch (type) {
    case 'campaigns':
      return <HugeiconsIcon icon={Megaphone02Icon} className={cls} />
    case 'media':
      return <HugeiconsIcon icon={VideoCameraAiIcon} className={cls} />
    case 'activity':
      return <HugeiconsIcon icon={TimeScheduleIcon} className={cls} />
    case 'conversations':
      return <HugeiconsIcon icon={Message01Icon} className={cls} />
    default:
      return null
  }
}

const itemClass =
  'flex cursor-pointer select-none items-center gap-2.5 rounded-lg px-2 py-2 text-sm text-atrium-black outline-none hover:bg-neutral-100 data-[selected=true]:bg-neutral-100'

export interface CommandSearchResultsProps {
  query: string
  results: SearchResultGroup[]
  loading: boolean
  showNav: boolean
  hasResults: boolean
  onSelectUrl: (url: string) => void
  onSelectThread: (threadId: string) => void
  onNewIntent: (intent: string) => void
}

export function CommandSearchResults({
  query,
  results,
  loading,
  showNav,
  hasResults,
  onSelectUrl,
  onSelectThread,
  onNewIntent,
}: CommandSearchResultsProps) {
  const navMatches = useMemo(() => filterNavShortcuts(query), [query])
  const showNavMatches = navMatches.length > 0

  return (
    <>
      {loading && (
        <div className="flex items-center justify-center gap-2 py-6 text-sm text-[#939294]">
          <HugeiconsIcon icon={Loading03Icon} className="size-4 animate-spin" />
          Searching…
        </div>
      )}

      {!loading && showNavMatches && (
        <>
          <Command.Group heading={TYPE_LABELS.navigate} className={groupHeadingClass}>
            {navMatches.map((item) => (
              <Command.Item
                key={item.id}
                value={`navigate-${item.id}`}
                onSelect={() => onSelectUrl(item.url)}
                className={itemClass}
              >
                <HugeiconsIcon icon={item.icon} className="size-4 shrink-0 text-[#939294]" />
                <span className="flex-1 truncate">{item.label}</span>
                <kbd className="shrink-0 text-[11px] text-[#939294]">↵</kbd>
              </Command.Item>
            ))}
          </Command.Group>
          {showNav && (
            <>
              <Command.Separator className="my-1 h-px bg-atrium-border mx-2" />
              <Command.Group heading={TYPE_LABELS.new} className={groupHeadingClass}>
                {NEW_SHORTCUTS.map((item) => (
                  <Command.Item
                    key={item.id}
                    value={`new-${item.id}`}
                    onSelect={() => onNewIntent(item.intent)}
                    className={itemClass}
                  >
                    <HugeiconsIcon icon={PlusSignIcon} className="size-4 text-accent-600 shrink-0" />
                    <span className="flex-1 truncate">{item.label}</span>
                    <kbd className="shrink-0 text-[11px] text-[#939294]">↵</kbd>
                  </Command.Item>
                ))}
              </Command.Group>
            </>
          )}
        </>
      )}

      {!loading && !showNavMatches && !hasResults && (
        <Command.Empty className="py-6 text-center text-sm text-[#939294]">
          No results for &ldquo;{query}&rdquo;
        </Command.Empty>
      )}

      {!loading &&
        !showNav &&
        results.map((group) => (
          <Command.Group
            key={group.type}
            heading={TYPE_LABELS[group.type] ?? group.type}
            className={groupHeadingClass}
          >
            {group.items.map((item) => (
              <Command.Item
                key={item.id}
                value={`${group.type}-${item.id}-${item.title}`}
                onSelect={() =>
                  group.type === 'conversations'
                    ? onSelectThread(item.id)
                    : onSelectUrl(item.url)
                }
                className={itemClass}
              >
                <ResultIcon type={group.type} />
                <span className="flex-1 truncate">{item.title}</span>
                {item.subtitle && group.type === 'campaigns' && (
                  <CampaignStatusBadge status={item.subtitle} />
                )}
                {item.subtitle && group.type !== 'campaigns' && (
                  <span className="ml-2 max-w-[140px] shrink-0 truncate text-xs text-[#939294]">
                    {item.subtitle}
                  </span>
                )}
              </Command.Item>
            ))}
          </Command.Group>
        ))}
    </>
  )
}
